import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Collapse,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { supabase } from "../../../../components/SupabaseClient";
import { useGroups } from "../../../../context/ResourceGroupsContext";

// A simple layout component to replicate the provided `ProfileRow` structure.
const ProfileRow = ({ label, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
      {label}
    </Typography>
    {children}
  </Box>
);

const AllocationCard = ({ allocation }) => {
  const {
    partition_name,
    interactive_local_queue,
    max_walltime_interactive,
    batch_local_queue,
    max_walltime_batch,
  } = allocation;
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1, boxShadow: "none" }}>
      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.5 }}>
        {partition_name}
      </Typography>
      <Box sx={{ display: "flex", gap: 3, mt: 0.5 }}>
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            Interactive
          </Typography>
          <Typography variant="body2">
            Queue: {interactive_local_queue || "Not configured"}
          </Typography>
          <Typography variant="body2">
            Max Walltime: {max_walltime_interactive}h
          </Typography>
        </Box>
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            Batch
          </Typography>
          <Typography variant="body2">
            Queue: {batch_local_queue || "Not configured"}
          </Typography>
          <Typography variant="body2">
            Max Walltime: {max_walltime_batch}h
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

const GroupDetails = ({ group }) => (
  <Box sx={{ p: 1.5 }}>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
      {group.description || ""}
    </Typography>
    <Box>
      {(group.allocations || []).map((alloc, idx) => (
        <AllocationCard key={idx} allocation={alloc} />
      ))}
    </Box>
  </Box>
);

const ResourceGroupsEditor = ({ userId, onMembershipChange }) => {
  const [assignedGroups, setAssignedGroups] = useState([]);
  const [assignedLoading, setAssignedLoading] = useState(false);
  const [assignedError, setAssignedError] = useState(null);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [durationInput, setDurationInput] = useState("");

  const {
    groups: allGroups,
    loading: allGroupsLoading,
    error: allGroupsError,
  } = useGroups();

  const fetchAssignedGroups = useCallback(async () => {
    if (!userId) return;

    setAssignedLoading(true);
    setAssignedError(null);

    try {
      const { data, error: groupsError } = await supabase.rpc(
        "get_user_groups",
        { p_user_id: userId },
      );

      if (groupsError) throw groupsError;

      setAssignedGroups(data || []);
    } catch (err) {
      console.error("Error fetching user groups:", err);
      setAssignedError(err.message || "Failed to fetch groups.");
      setAssignedGroups([]);
    } finally {
      setAssignedLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchAssignedGroups();
  }, [fetchAssignedGroups]);

  const handleRevoke = async (groupId) => {
    try {
      const { error: revokeError } = await supabase.rpc(
        "revoke_group_membership",
        {
          p_user_id: userId,
          p_group_id: groupId,
        },
      );

      if (revokeError) throw revokeError;

      // Refresh this user's assigned groups and the parent component (i.e., DashboardUsers)
      await fetchAssignedGroups();
      await onMembershipChange?.();
    } catch (err) {
      console.error("Error revoking group membership:", err);
      setAssignedError(err.message || "Failed to revoke membership.");
    }
  };

  const handleGrant = async () => {
    if (!editingGroupId) return;

    try {
      const { error: grantError } = await supabase.rpc(
        "grant_group_membership",
        {
          p_user_id: userId,
          p_group_id: editingGroupId,
          p_duration: durationInput.trim() || null,
        },
      );

      if (grantError) throw grantError;

      await fetchAssignedGroups();
      await onMembershipChange?.();
      setEditingGroupId(null);
      setDurationInput("");
    } catch (err) {
      console.error("Error granting group membership:", err);
      setAssignedError(err.message || "Failed to grant membership.");
    }
  };

  const handleAssignClick = (groupId) => {
    setEditingGroupId(groupId);
    setDurationInput("");
  };

  const handleCancelAssign = () => {
    setEditingGroupId(null);
    setDurationInput("");
  };

  const [expandedGroupIds, setExpandedGroupIds] = useState(new Set());

  const handleToggleExpand = (id) => {
    setExpandedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // get_user_groups returns one row per group membership. No group duplicates.
  const assignedGroupIds = useMemo(
    () => new Set(assignedGroups.map((g) => g.id)),
    [assignedGroups],
  );

  const unassignedGroups = useMemo(() => {
    return allGroups
      .filter((g) => !assignedGroupIds.has(g.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allGroups, assignedGroupIds]);

  if (assignedLoading || allGroupsLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  const error = assignedError || allGroupsError;
  if (error) {
    return <Alert severity="error">{error.message || error}</Alert>;
  }

  return (
    <>
      <ProfileRow label="Assigned Groups">
        {assignedGroups.length > 0 ? (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ boxShadow: "none" }}
          >
            <Table size="small" aria-label="assigned groups table">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell sx={{ fontWeight: "bold" }}>Group Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Granted At</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Expiration Date
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {assignedGroups.map((g) => {
                  const isExpanded = expandedGroupIds.has(g.id);
                  return (
                    <React.Fragment key={g.id}>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <IconButton
                            size="small"
                            onClick={() => handleToggleExpand(g.id)}
                          >
                            {isExpanded ? (
                              <KeyboardArrowUpIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowDownIcon fontSize="small" />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {g.name}
                        </TableCell>
                        <TableCell>{g.type}</TableCell>
                        <TableCell>
                          {g.granted_at
                            ? new Date(g.granted_at).toLocaleString()
                            : "N/A"}
                        </TableCell>
                        <TableCell>
                          {g.expires_at
                            ? new Date(g.expires_at).toLocaleString()
                            : "N/A"}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleRevoke(g.id)}
                            color="error"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 0, borderBottom: 0 }}>
                          <Collapse in={isExpanded} unmountOnExit>
                            <GroupDetails group={g} />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography>No groups assigned.</Typography>
        )}
      </ProfileRow>

      <ProfileRow label="Available to Assign">
        {unassignedGroups.length > 0 ? (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ boxShadow: "none" }}
          >
            <Table size="small" aria-label="unassigned groups table">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell sx={{ fontWeight: "bold" }}>Group Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Type</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {unassignedGroups.map((g) => {
                  const isExpanded = expandedGroupIds.has(g.id);
                  return (
                    <React.Fragment key={g.id}>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <IconButton
                            size="small"
                            onClick={() => handleToggleExpand(g.id)}
                          >
                            {isExpanded ? (
                              <KeyboardArrowUpIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowDownIcon fontSize="small" />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {g.name}
                        </TableCell>
                        <TableCell>{g.type}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleAssignClick(g.id)}
                            color="success"
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={4} sx={{ py: 0, borderBottom: 0 }}>
                          <Collapse in={isExpanded} unmountOnExit>
                            <GroupDetails group={g} />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                      {editingGroupId === g.id && (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            sx={{
                              p: 2,
                              borderBottom: "1px solid rgba(224, 224, 224, 1)",
                              backgroundColor: "rgba(0, 0, 0, 0.02)",
                            }}
                          >
                            <Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  mb: 2,
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: "bold" }}
                                >
                                  Assignment Settings
                                </Typography>
                                <Box>
                                  <Button
                                    onClick={handleCancelAssign}
                                    size="small"
                                    color="error"
                                    sx={{ mr: 1 }}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    onClick={handleGrant}
                                    variant="contained"
                                    size="small"
                                  >
                                    Submit
                                  </Button>
                                </Box>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ whiteSpace: "nowrap" }}
                                >
                                  Duration (optional):
                                </Typography>
                                <TextField
                                  fullWidth
                                  variant="outlined"
                                  size="small"
                                  value={durationInput}
                                  onChange={(e) =>
                                    setDurationInput(e.target.value)
                                  }
                                  placeholder="e.g., '1 year 2 months 3 days'"
                                />
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography>All available groups have been assigned.</Typography>
        )}
      </ProfileRow>
    </>
  );
};

export default ResourceGroupsEditor;
